// Enhanced analytics with comprehensive logging and deduplication
import mixpanel from './deferredMixpanel.js';

const __DEV__ = import.meta.env.DEV;
const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;
const MIXPANEL_TOKEN_PROD = import.meta.env.VITE_MIXPANEL_TOKEN_PROD;
const SESSION_REPLAY_ENABLED = import.meta.env.VITE_MIXPANEL_SESSION_REPLAY_ENABLED === 'true';
// TAS-311: replay capture serializes every DOM mutation — on a budget
// device it fights the animations for the main thread (field case
// 2026-08-21: 160 replay chunks recorded on the phone that lagged).
import { isLitePerf } from './perfTier.js';
import { setMetaPixelConsent, forwardToMetaPixel, scrubSensitiveParamsFromUrl, getScrubbedParam } from './metaPixel.js';
import { captureAndStripToken } from './attribution.js';
import { captureClaimHandoff, captureAuthFragment } from './claimHandoff.js';
export { getScrubbedParam };
const SESSION_REPLAY_PERCENT = Number(import.meta.env.VITE_MIXPANEL_SESSION_REPLAY_PERCENT ?? 0);

const _replayPercent =
  SESSION_REPLAY_ENABLED && Number.isFinite(SESSION_REPLAY_PERCENT)
    ? Math.max(0, Math.min(100, SESSION_REPLAY_PERCENT))
    : 0;

const _mixpanelConfig = {
  debug: import.meta.env.DEV,
  track_pageview: false,
  persistence: 'localStorage',
  cross_subdomain_cookie: false,
  secure_cookie: true,
  batch_requests: true,
  record_sessions_percent: isLitePerf() ? 0 : _replayPercent,
  // Session-replay PII defaults. (TAS-94)
  // Without these, flipping the env flag would start recording real user
  // sessions verbatim — including text inputs (emails, addresses, prize
  // codes), avatars / photos, and any element the rest of the app marks
  // as sensitive. Mask everything text-like by default; opt-in for
  // specific surfaces by adding `data-mixpanel-record="true"` or the
  // `.mp-record-text` class to elements that are safe to capture.
  record_mask_text_selector: 'input, textarea, select, [contenteditable], [data-pii]',
  record_block_selector: '[data-mixpanel-block], .mp-no-record',
  record_mask_text_class: 'mp-mask',
  record_collect_fonts: false,
  record_idle_timeout_ms: 30 * 60 * 1000,
  // EU data residency — events must go to EU ingestion endpoint
  api_host: 'https://api-eu.mixpanel.com',
};

// Pick the correct token at load so we never need to reinit.
// Production analytics only on a live Tasted address (tasted.co.za and its
// subdomains). A production BUILD also runs on localhost (Playwright against
// `vite preview`), on Vercel previews and on the staging aliases, and those
// sent about 12,000 events a month into the production project (Oct 2026).
// Anywhere else gets VITE_MIXPANEL_TOKEN, the staging project.
export function isLiveTastedHost(hostname) {
  const h = String(hostname || '').toLowerCase();
  return h === 'tasted.co.za' || h.endsWith('.tasted.co.za');
}
const _liveHost = (() => {
  try { return isLiveTastedHost(window.location.hostname); } catch { return false; }
})();
const _isProd = import.meta.env.MODE === 'production';
const _useProd = _isProd && _liveHost && Boolean(MIXPANEL_TOKEN_PROD);
const _initToken = _useProd ? MIXPANEL_TOKEN_PROD : MIXPANEL_TOKEN;
const _initSessionType = _useProd ? 'prod' : 'test';

// Which app this is (web, game, admin, cellar), set by each app's entry point
// through registerApp(). A super property, so EVERY event carries it and any
// report can be split or filtered by app; it is put back after a re-init.
let _appName = null;

// Track which token Mixpanel is currently initialized with so we can skip
// redundant re-inits. Re-calling mixpanel.init() spins up a second batch
// controller on the same `__mpq_<token>` localStorage queue; the two
// controllers then contend for the queue mutex, surfacing as recurring
// "[lock] Timeout waiting for mutex on __mpq_…" console errors.
let _activeToken = null;

// True whenever Mixpanel has been initialized with SOME token (dev or prod).
// Guards must use this — checking MIXPANEL_TOKEN alone silently disables
// tracking AND consent opt-out in builds that only set VITE_MIXPANEL_TOKEN_PROD.
const analyticsActive = () => Boolean(_activeToken);

// The /welcome claim handoff (`ct`, `player`, `code`) comes off the URL BEFORE
// Mixpanel initialises: init can start session replay, and every event after
// it stamps $current_url. See claimHandoff.js.
try { captureClaimHandoff(); } catch { /* ignore */ }

// A magic-link return (#access_token=…&refresh_token=…) comes off the URL here
// for the same reason, and it matters more: those are live credentials, and
// Mixpanel stamps $current_url from window.location.href on EVERY event no
// matter what any caller passes. Sanitising our own `hash` property cannot
// reach that, and 2.63.0 has no sanitize_properties hook, so the fragment has
// to be gone before init. consumeMagicLinkHash() picks it up from the stash.
// This runs for every app that loads analytics: web /redeem and /account,
// admin /reset, the game's /welcome and /claim.
try { captureAuthFragment(); } catch { /* ignore */ }

if (_initToken) {
  mixpanel.init(_initToken, _mixpanelConfig);
  mixpanel.register({ environment: import.meta.env.MODE, session_type: _initSessionType });
  _activeToken = _initToken;
} else {
  console.warn(
    '[ANALYTICS] Mixpanel token not found. Analytics will be disabled.'
  );
}

/**
 * Re-initializes Mixpanel with the correct project token for the given
 * session type ('prod' | 'test'). Call this once the game session is loaded.
 * No-op if VITE_MIXPANEL_TOKEN_PROD is not set (e.g. admin/web apps).
 */
export function reinitForSessionType(sessionType) {
  // A prod game played on a preview, staging or a laptop still reports to the
  // staging project (see isLiveTastedHost).
  const token =
    sessionType === 'prod' && MIXPANEL_TOKEN_PROD && _liveHost
      ? MIXPANEL_TOKEN_PROD
      : MIXPANEL_TOKEN;
  if (!token) return;
  // Skip the redundant re-init when the token is unchanged (the common case:
  // test sessions already initialized with the same token at module load).
  // Re-initializing would create a duplicate batch controller that fights the
  // existing one for the queue mutex. Only the session_type super-property
  // needs refreshing in that case.
  if (token === _activeToken) {
    mixpanel.register({ environment: import.meta.env.MODE, session_type: sessionType });
    return;
  }
  mixpanel.init(token, _mixpanelConfig);
  mixpanel.register({ environment: import.meta.env.MODE, session_type: sessionType });
  if (_appName) mixpanel.register({ app: _appName });
  _activeToken = token;
  // A fresh init resets opt-out state — re-honor a prior "decline". (TAS-252)
  try { if (getTrackingConsent() === 'denied') mixpanel.opt_out_tracking(); } catch { /* ignore */ }
}

// Identity props that only some events carry, added to the dedupe key for
// that event alone. Final Card Viewed is sent once per card, with the card in
// `card`; without it a player paging through the final cards inside 5s keyed
// every card the same, and all but the first were dropped (final-page review
// 2026-10-07). The same card twice inside 5s is still a repeat.
const EVENT_IDENTITY_PROPS = {
  'Final Card Viewed': ['card'],
};

// Event deduplication system
class EventDeduplicator {
  constructor() {
    this.recentEvents = new Map();
    this.sessionEvents = new Set();
    this.cleanup();
  }

  // Generate unique event key for deduplication.
  //
  // TAS-320: the key used to be event+session+user+path only, so two events
  // of the same kind from one player on one route inside the 5s window were
  // indistinguishable and the second was dropped. On a fast round that cost
  // ~12% of Answer Submitted (session 4679: 189 answers in the DB, 167 in
  // Mixpanel) and 3 of 27 Wine Review Submitted. The identity-bearing props
  // below are what actually makes two of these events DIFFERENT, so they
  // belong in the key; a caller with its own notion of identity can pass
  // `dedupeKey` and skip the guesswork.
  generateEventKey(event, props = {}) {
    const sessionCode = props.sessionCode || 'no-session';
    const userId =
      props.userId ||
      (analyticsActive() ? mixpanel.get_distinct_id() : null) ||
      'anonymous';
    const path = props.path || window.location.pathname;
    // Values are normalized to strings: a questionId that arrives as 1 from one
    // call site and '1' from another must not key as two different events.
    const identity =
      props.dedupeKey ??
      [
        props.questionId,
        props.wineId,
        props.roundId,
        props.stepIndex,
        ...(EVENT_IDENTITY_PROPS[event] ?? []).map((k) => props[k]),
      ]
        .filter((v) => v !== undefined && v !== null && v !== '')
        .map((v) => String(v))
        .join('|');

    const base = `${event}:${sessionCode}:${userId}:${path}`;
    return identity ? `${base}:${identity}` : base;
  }

  // Check if event is duplicate within time window
  isDuplicate(eventKey, timeWindow = 5000) {
    const now = Date.now();
    const lastTime = this.recentEvents.get(eventKey);

    if (lastTime && now - lastTime < timeWindow) {
      return true;
    }

    this.recentEvents.set(eventKey, now);
    return false;
  }

  // Check if event already fired in this session
  isSessionDuplicate(eventKey) {
    if (this.sessionEvents.has(eventKey)) {
      return true;
    }
    this.sessionEvents.add(eventKey);
    return false;
  }

  // Clean up old events
  cleanup() {
    setInterval(() => {
      const now = Date.now();
      const fiveMinutesAgo = now - 300000; // 5 minutes

      for (const [key, timestamp] of this.recentEvents.entries()) {
        if (timestamp < fiveMinutesAgo) {
          this.recentEvents.delete(key);
        }
      }
    }, 60000); // Clean every minute
  }

  // Clear session events (call on new session)
  clearSession() {
    this.sessionEvents.clear();
  }
}

const deduplicator = new EventDeduplicator();

// Enhanced logging system
class AnalyticsLogger {
  constructor() {
    this.logs = [];
    this.maxLogs = 1000;
    this.sessionId = this.generateSessionId();
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  log(level, message, data = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      data,
      sessionId: this.sessionId,
      // Sanitize URL — never log query/hash from the logger either.
      url: (typeof window !== 'undefined' && window.location && window.location.href)
        ? `${window.location.origin}${window.location.pathname}`
        : '',
      userAgent: navigator.userAgent,
    };

    this.logs.push(logEntry);

    // Keep only recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }

    // Console logging with colors (dev only)
    if (__DEV__) {
      const colors = {
        info: 'color: #2196F3',
        warn: 'color: #FF9800',
        error: 'color: #F44336',
        success: 'color: #4CAF50',
        duplicate: 'color: #9C27B0',
        track: 'color: #00BCD4',
      };

      console.log(
        `%c[ANALYTICS:${level.toUpperCase()}] ${message}`,
        colors[level] || '',
        data
      );
    }
  }

  // Get all logs for debugging
  getAllLogs() {
    return this.logs;
  }

  // Get logs by level
  getLogsByLevel(level) {
    return this.logs.filter((log) => log.level === level);
  }

  // Export logs for analysis
  exportLogs() {
    const blob = new Blob([JSON.stringify(this.logs, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-logs-${this.sessionId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Clear logs
  clearLogs() {
    this.logs = [];
    this.log('info', 'Logs cleared');
  }
}

const logger = new AnalyticsLogger();

// Global context merged into every analytics event
let globalContext = {};

/**
 * Register Mixpanel super properties — attached to every subsequent event on
 * this device, including ones fired by code that knows nothing about them.
 *
 * Distinct from setSessionContext above, which only decorates events routed
 * through this module's track(). Super properties survive into Mixpanel's own
 * automatic events and persist across page loads, which is what cross-origin
 * attribution needs: the web app is a different origin from the game, so a
 * buyer arriving from a game otherwise looks like a brand-new anonymous user.
 *
 * Null/undefined values are dropped rather than registered — Mixpanel would
 * otherwise store them and they'd show up as empty breakdown buckets forever.
 */
export const registerSuperProperties = (props = {}, { replaceKeys } = {}) => {
  try {
    const clean = Object.fromEntries(
      Object.entries(props).filter(([, v]) => v !== null && v !== undefined && v !== ''),
    );
    // `replaceKeys` names a SET that is registered as a whole: anything in it
    // without a value now is unregistered rather than left standing. Attribution
    // needs this — a later visit carrying only a source would otherwise keep the
    // previous visit's player and session registered, and a stale super property
    // is worse than a missing one: it is a wrong answer that looks right.
    if (Array.isArray(replaceKeys)) {
      const drop = replaceKeys.filter((k) => !(k in clean));
      if (drop.length) unregisterSuperProperties(drop);
    }
    if (Object.keys(clean).length === 0) return;
    mixpanel.register(clean);
    logger.log('info', 'Registered super properties', clean);
  } catch (error) {
    logger.log('error', 'Failed to register super properties', { error: error?.message });
  }
};

/** Forget super properties by name. No-op for ones that were never set. */
export const unregisterSuperProperties = (names = []) => {
  try {
    for (const name of names) mixpanel.unregister(name);
    logger.log('info', 'Unregistered super properties', { names });
  } catch (error) {
    logger.log('error', 'Failed to unregister super properties', { error: error?.message });
  }
};

// Set session-wide context to be included on all future events
export const setSessionContext = (context = {}) => {
  try {
    globalContext = {
      ...globalContext,
      ...context,
      // Normalize keys: prefer sessionCode and wines array
      sessionCode: context.sessionCode || context.session_id || globalContext.sessionCode,
      wines: Array.isArray(context.wines) ? context.wines : globalContext.wines,
      userId: context.userId || globalContext.userId,
    };
    logger.log('info', 'Session context set', globalContext);

    // Persist helpful user/session props on the Mixpanel profile
    if (analyticsActive()) {
      const peopleProps = {
        sessionCode: globalContext.sessionCode || null,
        game_user_id: globalContext.userId || null,
        session_wines: Array.isArray(globalContext.wines)
          ? globalContext.wines
          : null,
      };
      try {
        mixpanel.people.set(peopleProps);
        logger.log('success', 'Mixpanel people props set', peopleProps);
      } catch (e) {
        logger.log('warn', 'Failed to set Mixpanel people props', {
          error: e.message,
          peopleProps,
        });
      }
    }
  } catch (error) {
    logger.log('error', 'Failed to set session context', { error: error.message });
  }
};

// Strip query + hash from a URL so analytics never carries auth tokens,
// magic-link codes, prize codes, etc. — the codebase deliberately avoids
// query-param PII elsewhere. (TAS-92)
const sanitizeUrl = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  try {
    const u = new URL(raw);
    return u.origin + u.pathname;
  } catch {
    // Relative URLs / weird input: drop everything after the first ? or #.
    return raw.split(/[?#]/)[0];
  }
};

// Same for referrer — only carry origin+path, never query/hash from upstream.
const sanitizeReferrer = (raw) => sanitizeUrl(raw);

// Listeners that see every event that passes the dedupe gate below, whether or
// not Mixpanel is active. apps/web uses one to feed its own site counter. A
// listener that throws is ignored: it can never break tracking.
const trackListeners = new Set();
export const onTrack = (fn) => {
  if (typeof fn !== 'function') return () => {};
  trackListeners.add(fn);
  return () => {
    trackListeners.delete(fn);
  };
};

/**
 * Label every event from this app with `app` (web, game, admin, cellar).
 * Call once from the app's entry point, before anything is tracked.
 */
export function registerApp(name) {
  if (typeof name !== 'string' || !name) return;
  _appName = name;
  if (!analyticsActive()) return;
  try { mixpanel.register({ app: name }); } catch { /* ignore */ }
}

// Enhanced tracking function with deduplication and logging.
//
// `options` goes to the SDK untouched. The one use today is a last event as
// the tab goes away: { transport: 'sendBeacon', send_immediately: true } skips
// the batch queue and hands the request to the browser, which delivers it
// after the page has gone. Without it the event waits in the queue for a page
// load that may never come.
export const track = (event, props = {}, options) => {
  try {
    // Add metadata. Note `url` / `referrer` are sanitized to origin+path —
    // never carry the query string. (TAS-92)
    const enhancedProps = {
      ...globalContext,
      ...props,
      timestamp: new Date().toISOString(),
      sessionId: logger.sessionId,
      url: sanitizeUrl(window.location.href),
      referrer: sanitizeReferrer(document.referrer),
      userAgent: navigator.userAgent,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    };

    const eventKey = deduplicator.generateEventKey(event, enhancedProps);
    // `dedupeKey` is a caller hint for the key above, never a real property —
    // strip it so it can't ride into Mixpanel's lexicon. (TAS-320)
    delete enhancedProps.dedupeKey;

    // Check for duplicates
    if (deduplicator.isDuplicate(eventKey)) {
      logger.log('duplicate', `Duplicate event blocked: ${event}`, {
        event,
        props: enhancedProps,
        eventKey,
        reason: 'Time-based duplicate (within 5s)',
      });
      return;
    }

    // Log the tracking attempt
    logger.log('track', `Tracking event: ${event}`, {
      event,
      props: enhancedProps,
      eventKey,
    });

    trackListeners.forEach((fn) => {
      try {
        fn(event, enhancedProps);
      } catch {
        // a listener must never break tracking
      }
    });

    // Send to Mixpanel (only if initialized)
    if (analyticsActive()) {
      if (options) mixpanel.track(event, enhancedProps, options);
      else mixpanel.track(event, enhancedProps);
      // Meta gets the same event, after the same dedupe gate and only for the
      // handful of names in its map (TAS-338). Sitting here rather than at the
      // call sites means Meta can never see something Mixpanel didn't, and
      // there is one place to audit what leaves for an ad platform.
      forwardToMetaPixel(event, enhancedProps);
      logger.log('success', `Event sent successfully: ${event}`, {
        event,
        eventKey,
      });
    } else {
      logger.log('info', `Analytics disabled - would have tracked: ${event}`, {
        event,
        props: enhancedProps,
        eventKey,
      });
    }
  } catch (error) {
    logger.log('error', `Failed to track event: ${event}`, {
      event,
      props,
      error: error.message,
      stack: error.stack,
    });
  }
};

// Increment a numeric user property on the Mixpanel profile
export const incrementUserProperty = (propertyName, amount = 1) => {
  try {
    if (!analyticsActive()) {
      logger.log('info', 'Analytics disabled - would have incremented people property', {
        propertyName,
        amount,
      });
      return;
    }
    mixpanel.people.increment(propertyName, amount);
    logger.log('success', 'Incremented Mixpanel people property', {
      propertyName,
      amount,
    });
  } catch (error) {
    logger.log('error', 'Failed to increment Mixpanel people property', {
      propertyName,
      amount,
      error: error.message,
    });
  }
};

// Enhanced page tracking.
//
// Deduplication is track()'s job alone. The old pre-check here registered
// the event key and then track() re-checked the SAME key microseconds
// later — so every single Page View was self-blocked as a "duplicate" and
// none ever reached Mixpanel (dead since 2025-10, found 2026-08-21).
export const trackPageView = (path, additionalProps = {}) => {
  // Entry-source attribution: lift utm_* out of the query string onto the
  // event AND the super props, so later events (Checkout Started, Order
  // Placed…) carry the source too. Only the five utm keys — never the full
  // query string (TAS-92).
  const utm = {};
  try {
    const q = new URLSearchParams(window.location.search);
    // Values are URL-controlled by whoever shares a link: sanitize to a
    // conservative slug so pasted free text (or an email address) can never
    // ride into Mixpanel. utm_term is dropped entirely — we don't buy
    // keywords. (Codex, 2026-08-21)
    const slug = (v) => String(v).toLowerCase().replace(/[^a-z0-9_.-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
    for (const k of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content']) {
      const v = q.get(k);
      if (v) utm[k] = slug(v);
    }
    // Legacy tagged links (game buy-box, recap emails) use `src=` instead of
    // utm_*: fold them into utm_source so every entry point lands in ONE
    // breakdown. Explicit utm_source always wins.
    if (!utm.utm_source) {
      const src = q.get('src');
      if (src) { utm.utm_source = slug(src); utm.utm_medium = utm.utm_medium || 'referral'; }
    }
    // Paid-media identifiers (TAS-340). Meta's URL-parameter macros expand to
    // these at click time. Without them a funnel cannot be broken down by
    // individual ad, which is the entire read a 10-ad split test exists to
    // produce — utm_content alone collapses once two ads share a name.
    // Numeric-only by construction, so the slug above is not the right filter:
    // these are ids, and anything that isn't digits is someone editing a URL.
    for (const k of ['ad_id', 'adset_id', 'campaign_id']) {
      const v = q.get(k);
      if (v && /^\d{1,25}$/.test(v)) utm[k] = v;
    }
    // fbclid marks a click that came from Meta (TAS-339). It is BOTH the
    // proof this visit was paid — the morning report's Ad Click step keys off
    // it — and the match key the Conversions API needs to tie a server-side
    // purchase back to the ad that earned it. Meta's own format is a bounded
    // token; anything longer or stranger is not one, so it is dropped rather
    // than forwarded.
    const fbclid = q.get('fbclid');
    if (fbclid && /^[A-Za-z0-9._-]{1,255}$/.test(fbclid)) {
      utm.fbclid = fbclid;
      utm.paid_click = true;
      if (!utm.utm_source) { utm.utm_source = 'facebook'; utm.utm_medium = utm.utm_medium || 'paid'; }
    }
    // Visit-scoped, NOT persisted: a permanent register() would stamp next
    // week's direct visit with this week's email campaign. (Codex)
    if (Object.keys(utm).length && analyticsActive()) mixpanel.register(utm, { persistent: false });
  } catch { /* malformed URL — track without attribution */ }

  track('Page View', {
    ...globalContext,
    path,
    title: document.title,
    ...utm,
    ...additionalProps,
  });
};

// Enhanced screen tracking for specific screens
export const trackScreenView = (screenName, additionalProps = {}) => {
  const props = {
    screenName,
    ...additionalProps,
  };

  track(`${screenName} Screen Viewed`, props);
};

// User identification with logging - temporary game username
export const identifyWithName = (username) => {
  try {
    if (!analyticsActive()) {
      logger.log(
        'info',
        `Analytics disabled - would have identified user: ${username}`,
        {
          username,
          identityType: 'temporary_name',
        }
      );
      return;
    }

    const distinctId = mixpanel.get_distinct_id();

    logger.log('info', `Identifying user with temporary name: ${username}`, {
      username,
      distinctId,
      previousId: distinctId,
      identityType: 'temporary_name',
    });

    // Store the original anonymous ID for later merging
    localStorage.setItem('mixpanel_anonymous_id', distinctId);
    localStorage.setItem('mixpanel_temp_username', username);

    // Use alias to link temporary username to anonymous ID
    mixpanel.alias(username);
    mixpanel.people.set({
      username,
      $name: username,
      identity_type: 'temporary_game_user',
    });

    logger.log('success', `User identified with temporary name: ${username}`, {
      username,
      distinctId,
      identityType: 'temporary_name',
    });

    // Don't clear session events - we want to preserve the anonymous journey
  } catch (error) {
    logger.log('error', `Failed to identify user with name: ${username}`, {
      username,
      error: error.message,
    });
  }
};

// Enhanced user identification with proper identity merging
export const identifyWithUserId = (userId, userProps = {}) => {
  try {
    if (!analyticsActive()) {
      logger.log(
        'info',
        `Analytics disabled - would have identified user with ID: ${userId}`,
        {
          userId,
          userProps,
          identityType: 'registered_user',
        }
      );
      return;
    }

    const currentDistinctId = mixpanel.get_distinct_id();
    const anonymousId = localStorage.getItem('mixpanel_anonymous_id');
    const tempUsername = localStorage.getItem('mixpanel_temp_username');

    logger.log('info', `Identifying user with real ID: ${userId}`, {
      userId,
      userProps,
      currentDistinctId,
      anonymousId,
      tempUsername,
      identityType: 'registered_user',
    });

    // CRITICAL: Proper identity merging sequence
    if (anonymousId && anonymousId !== userId) {
      // Step 1: Alias the real user ID to the anonymous/temporary ID
      // This merges all anonymous events with the real user
      logger.log('info', `Merging identities: ${anonymousId} → ${userId}`, {
        from: anonymousId,
        to: userId,
        tempUsername,
      });

      mixpanel.alias(userId);
    }

    // Step 2: Set the real user identity
    mixpanel.identify(userId);

    // Step 3: Set comprehensive user properties
    const enhancedProps = {
      $name: userProps.name || userProps.username || tempUsername || '',
      $email: userProps.email || '',
      identity_type: 'registered_user',
      registration_date: new Date().toISOString(),
      had_anonymous_activity: !!anonymousId,
      had_temp_username: !!tempUsername,
      temp_username: tempUsername || null,
      ...userProps,
    };

    mixpanel.people.set(enhancedProps);

    // Strip raw PII before emitting the EVENT — `enhancedProps` carries
    // $name, $email, plus whatever userProps the caller passed (often
    // includes phone, address, etc.). Those belong on the people profile
    // (the mixpanel.people.set call above) but must NOT enter the event
    // stream where every downstream tool sees them. (TAS-91)
    const safeUserProperties = (() => {
      const out = {};
      const skipKeys = new Set([
        '$name', '$email', '$phone', 'name', 'email', 'phone',
        'username', 'first_name', 'last_name', 'address',
        // Common avatar / personal fields
        'avatar_url', 'photo_url',
      ]);
      for (const [k, v] of Object.entries(enhancedProps)) {
        if (skipKeys.has(k)) continue;
        out[k] = v;
      }
      // Surface the *flags* (had we identifying info?) without the values.
      out.had_name = Boolean(enhancedProps.$name || enhancedProps.name);
      out.had_email = Boolean(enhancedProps.$email || enhancedProps.email);
      return out;
    })();

    // Track the successful identity merge
    track('Identity Merged', {
      userId,
      anonymousId,
      tempUsername,
      mergeType: anonymousId
        ? 'anonymous_to_registered'
        : 'direct_registration',
      userProperties: safeUserProperties,
    });

    logger.log('success', `User identity merged successfully: ${userId}`, {
      userId,
      anonymousId,
      tempUsername,
      enhancedProps,
    });

    // Clean up temporary identity storage
    localStorage.removeItem('mixpanel_anonymous_id');
    localStorage.removeItem('mixpanel_temp_username');

    // Clear session events for new user identity
    deduplicator.clearSession();
  } catch (error) {
    logger.log('error', `Failed to identify user with ID: ${userId}`, {
      userId,
      userProps,
      error: error.message,
    });
  }
};

// ── Signup identity + event (TAS-251) ────────────────────────────────────────
// Identify the Mixpanel visitor as this account WITHOUT any PII — unlike
// identifyWithUserId, this never puts $name/$email/username on the profile
// (TAS-73/91). Merges prior anonymous activity into the account, then identifies
// so events (and Mixpanel's IP-geo) attach to the account. Use on sign-in.
export const identifyUser = (userId) => {
  if (!userId || !analyticsActive()) return;
  try {
    const anonymousId = localStorage.getItem('mixpanel_anonymous_id');
    if (anonymousId && anonymousId !== userId) {
      try { mixpanel.alias(userId); } catch { /* alias only valid pre-identify */ }
    }
    mixpanel.identify(userId);
    mixpanel.people.set({ identity_type: 'registered_user' }); // no PII
  } catch (e) {
    logger.log('warn', 'identifyUser failed', { userId, error: e?.message });
  }
};

// Fire "Account Created" for a GENUINELY NEW account. MUST run client-side so
// Mixpanel reads the user's IP for geo, not a server's. Guards: (1) only within
// 10 min of account creation, so returning logins never emit it; (2) once per
// account per browser. (Cross-device within that 10-min window can double-count
// — acceptable for an approximate signup metric.) `source` is non-PII only.
export const trackAccountCreated = (userId, createdAt, source) => {
  if (!userId) return;
  const created = createdAt ? new Date(createdAt).getTime() : 0;
  if (!created || Date.now() - created > 10 * 60 * 1000) return;
  const key = `tasted_acct_created_${userId}`;
  try {
    if (localStorage.getItem(key)) return; // already counted this account here
    localStorage.setItem(key, '1');
  } catch { /* private mode: fall through and still fire once this load */ }
  track('Account Created', { source: source || 'unknown' });
};

/**
 * An identity safe to hand to another Tasted origin, or null (TAS-341).
 *
 * Deliberately NOT the raw distinct_id. Mixpanel refuses `$device:`-prefixed
 * values as an identify() target — mixpanel-core.js reports
 * "distinct_id cannot have $device: prefix" and returns -1 — so handing over an
 * anonymous device id produces a merge that silently does nothing. Only a real,
 * already-identified id can be adopted on the other side, so only that is
 * offered. An anonymous player still carries `src`/`session`/`player` as
 * journey properties; they just are not merged into one person.
 * (Codex pair-review, 2026-08-29.)
 */
export const getShareableIdentity = () => {
  try {
    if (!analyticsActive()) return null;
    const id = mixpanel.get_distinct_id();
    if (!id || String(id).startsWith('$device:')) return null;
    return id;
  } catch { return null; }
};

/**
 * Adopt a distinct_id handed over from another Tasted origin (TAS-341).
 *
 * The game and the website are separate origins, so Mixpanel's distinct_id —
 * localStorage-scoped — does not cross between them. A player who taps "Buy a
 * Tasting Box" mid-party arrived on the website as a brand-new person, and a
 * funnel spanning both counted them twice. The `?src=game` properties told us
 * the visit came from a game; they could not tell us it was the SAME human.
 *
 * mixpanel.identify() sends $identify with the current anonymous id as
 * $anon_distinct_id, so this merges this browser's web-side history into the
 * game's profile rather than starting a new one.
 *
 * Two guards, both load-bearing:
 *  - Only while still anonymous. Mixpanel marks an un-identified id
 *    `$device:<uuid>`; if this visitor has already signed in, merging a shared
 *    party phone's game id into their account would be wrong and unpickable.
 *  - The id arrives in a URL anyone can edit, so it must look like a Mixpanel
 *    id and nothing else before it is trusted.
 */
export const adoptDistinctId = (incoming) => {
  if (!incoming || !analyticsActive()) return false;
  // A `$device:` value can never be adopted: mixpanel.identify() rejects it
  // outright and returns -1. Accepting one here is what made the first version
  // of this a no-op that reported success. (Codex pair-review, 2026-08-29)
  if (String(incoming).startsWith('$device:')) return false;
  if (!/^[A-Za-z0-9-]{8,64}$/.test(incoming)) return false;
  try {
    const current = mixpanel.get_distinct_id();
    if (current === incoming) return false;
    if (current && !String(current).startsWith('$device:')) return false; // already identified
    // identify() returns -1 when Mixpanel refuses. Trust the return value
    // rather than the absence of a throw.
    if (mixpanel.identify(incoming) === -1) {
      logger.log('error', 'Mixpanel refused the cross-origin identity', { incoming });
      return false;
    }
    logger.log('success', 'Adopted cross-origin distinct_id', { incoming });
    return true;
  } catch {
    return false;
  }
};

// ── Cookie / tracking consent (TAS-252) ──────────────────────────────────────
// Analytics runs by default (implied consent + a visible notice); an explicit
// "decline" opts the visitor out of Mixpanel and clears its state. Per-browser.
const TRACKING_CONSENT_KEY = 'tasted_tracking_consent'; // 'granted' | 'denied'

export const getTrackingConsent = () => {
  try { return localStorage.getItem(TRACKING_CONSENT_KEY); } catch { return null; }
};

export const setTrackingConsent = (granted) => {
  try { localStorage.setItem(TRACKING_CONSENT_KEY, granted ? 'granted' : 'denied'); } catch { /* ignore */ }
  // The Meta pixel obeys the same switch, and does so BEFORE the early return
  // below: the web app sets no Mixpanel token in some builds, and a visitor's
  // "Decline" must still stop the ad pixel in those. (TAS-338)
  setMetaPixelConsent(granted);
  if (!analyticsActive()) return;
  try {
    if (granted) mixpanel.opt_in_tracking();
    else mixpanel.opt_out_tracking();
  } catch { /* ignore */ }
};

// Honor a prior "decline" as soon as this module loads (init already ran above).
try {
  if (analyticsActive() && getTrackingConsent() === 'denied') mixpanel.opt_out_tracking();
} catch { /* ignore */ }

// Meta pixel: EXPLICIT consent only. Mixpanel keeps its implied-consent model
// (TAS-252) — an advertising pixel does not get it, so a visitor who has not
// answered the notice yet is treated as a no, not a yes. Inert unless
// VITE_META_PIXEL_ID is set, so this is a no-op everywhere the ad account
// doesn't exist yet, including the game and admin apps. (TAS-338)
try {
  setMetaPixelConsent(getTrackingConsent() === 'granted');
} catch { /* ignore */ }

// Strip email addresses and analytics ids off the landing URL regardless of the
// pixel, and before the first Page View. The recap prize links land on
// `?email=<address>`; that value has no business in ANY analytics event's
// `$current_url`, Mixpanel's included. (Codex pair-review, 2026-08-29)
try { scrubSensitiveParamsFromUrl(); } catch { /* ignore */ }

// Same reasoning for the attribution token (TAS-371): Mixpanel stamps
// $current_url onto every event, so a `t=` left in the address bar rides into
// analytics all visit carrying the player's uuid. Captured here — before the
// first track() — and handed to whichever app asks for it. Only a token that
// parses is taken, so /unsubscribe keeps its own `t=`.
try { captureAndStripToken(); } catch { /* ignore */ }

// Identity management utilities
export const identityUtils = {
  // Get current identity info
  getCurrentIdentity: () => {
    const distinctId = analyticsActive()
      ? mixpanel.get_distinct_id()
      : 'anonymous';
    const anonymousId = localStorage.getItem('mixpanel_anonymous_id');
    const tempUsername = localStorage.getItem('mixpanel_temp_username');

    return {
      distinctId,
      anonymousId,
      tempUsername,
      hasAnonymousHistory: !!anonymousId,
      hasTempUsername: !!tempUsername,
    };
  },

  // Check if identity merge is pending
  isPendingMerge: () => {
    const anonymousId = localStorage.getItem('mixpanel_anonymous_id');
    return !!anonymousId;
  },

  // Manual identity merge (for debugging)
  manualMerge: (userId, userProps = {}) => {
    logger.log('info', 'Manual identity merge triggered', {
      userId,
      userProps,
    });
    identifyWithUserId(userId, userProps);
  },

  // Clear stored identity data
  clearStoredIdentity: () => {
    localStorage.removeItem('mixpanel_anonymous_id');
    localStorage.removeItem('mixpanel_temp_username');
    logger.log('info', 'Stored identity data cleared');
  },
};

// Debug utilities
export const debugAnalytics = {
  // Get all logs
  getLogs: () => logger.getAllLogs(),

  // Get logs by level
  getLogsByLevel: (level) => logger.getLogsByLevel(level),

  // Export logs
  exportLogs: () => logger.exportLogs(),

  // Clear logs
  clearLogs: () => logger.clearLogs(),

  // Get duplicate events
  getDuplicates: () => logger.getLogsByLevel('duplicate'),

  // Get recent events
  getRecentEvents: () => Array.from(deduplicator.recentEvents.entries()),

  // Get session events
  getSessionEvents: () => Array.from(deduplicator.sessionEvents),

  // Manual event tracking (bypasses deduplication)
  forceTrack: (event, props) => {
    logger.log(
      'warn',
      `Force tracking event (bypassing deduplication): ${event}`,
      { event, props }
    );
    if (analyticsActive()) {
      // `dedupeKey` is a hint for the deduplicator, never a real property — and
      // forceTrack bypasses the deduplicator entirely, so strip it here too
      // rather than let it into the lexicon. (TAS-320, Codex review)
      const { dedupeKey: _dedupeKey, ...sendable } = props ?? {};
      mixpanel.track(event, sendable);
    } else {
      logger.log(
        'info',
        `Analytics disabled - would have force tracked: ${event}`,
        {
          event,
          props,
        }
      );
    }
  },

  // Clear session deduplication
  clearSession: () => {
    deduplicator.clearSession();
    logger.log('info', 'Session deduplication cleared');
  },

  // Identity debugging
  identity: identityUtils,
};

// Make debug utilities available globally in development
if (import.meta.env.DEV) {
  window.debugAnalytics = debugAnalytics;
  window.mixpanel = mixpanel;
  logger.log(
    'info',
    'Analytics debug utilities available at window.debugAnalytics'
  );
}

export { logger };
